import { useCubeStore } from '@/stores/useCubeStore'
import type { HistoryDataState } from '@/types/HistoryData'
import Chart, { type ChartConfiguration } from 'chart.js'
import moment from 'moment'
import { onBeforeUnmount, onMounted, ref, type Ref, type TemplateRef } from 'vue'

export type ChartJS = Chart & { tooltip: any; draw: () => void }

export function useChart(chartEl: TemplateRef<HTMLCanvasElement>) {
  const chartJs: Ref<ChartJS> = ref(null)

  let tooltipTimeout: number | null = null

  function sumArrays(array1, array2) {
    if (!array1 || !array2) {
      return []
    }
    const sum = []
    for (let i = 0; i < array1.length; i++) {
      sum.push(array1[i] + array2[i])
    }
    return sum
  }

  function setupEventHandlers(chart: ChartJS, chartEl: HTMLCanvasElement) {
    // On mobile: keep tooltip only while touching
    chartEl.addEventListener('touchend', function () {
      if (!!chart) {
        if (tooltipTimeout != null) {
          clearTimeout(tooltipTimeout)
        }

        tooltipTimeout = setTimeout(() => {
          chart.tooltip._active = []
          chart.tooltip.update(true)
          chart.draw()
        }, 2000)
      }
    })

    // Desktop: clicking empty space closes tooltip
    // chartEl.addEventListener('click', (evt) => {
    //   const elements: any[] = chart.getElementAtEvent(evt)

    //   if (!!chart && elements.length === 0) {
    //     chart.tooltip._active = []
    //     chart.tooltip.update(true)
    //     chart.draw()
    //   }
    // })
  }

  function updateMonthChart(_data, config): void {
    const data = JSON.parse(JSON.stringify(_data))

    // Don't show/update the chart without any data.
    if (
      data.labels.length < 1 ||
      !data.datasets.usageFromCommunity ||
      !data.datasets.forecastBalance
    ) {
      return;
    }

    const chartSettings = {
      ...config
    }

    chartSettings.plugins = [
      ...(chartSettings.plugins ?? []),
      {
        afterUpdate(chart: any) {
          const y = chart.scales['yAbove']
          const yPercent = chart.scales['yPercent']

          if (y && yPercent) {
            const min = y.min
            const max = y.max

            const oldMin = yPercent.options.ticks.min
            const oldMax = yPercent.options.ticks.max
            const newMin = (100 * min) / max
            const newMax = 100

            if (oldMin != newMin && oldMax != newMax) {
              yPercent.options.ticks.min = newMin
              yPercent.options.ticks.max = newMax

              // not ideal because it triggers a re-render, but better than nothing
              chart.update()
            }
          }
        }
      }
    ]

    chartSettings.data.labels = data.labels
    chartSettings.data.datasets[0].data = data.datasets.usageFromCommunity
    chartSettings.data.datasets[1].data = data.datasets.usageFromSupplier
    chartSettings.data.datasets[2].data = data.datasets.usageToCommunity
    chartSettings.data.datasets[3].data = data.datasets.usageToSupplier
    chartSettings.data.datasets[4].data = data.datasets.usageToGrid
    chartSettings.data.datasets[5].data = data.datasets.usageFromGrid

    const inverterIndex = chartSettings.data.datasets.findIndex((dataset) => {
      return dataset.label?.toLowerCase().includes('inverter') || dataset.label?.toLowerCase().includes('wechselrichter')
    })

    if (inverterIndex > 0 && useCubeStore().meterType !== 'Meterless') {
      chartSettings.data.datasets[inverterIndex].data = data.datasets.usageInverterProduction
    } else if (chartSettings.data.datasets[7]) {
      chartSettings.data.datasets[7].data = data.datasets.forecastBalance
    } else if (chartSettings.data.datasets[6]) {
      chartSettings.data.datasets[6].data = data.datasets.forecastBalance
    }

    const maxValue = Math.max(
      ...sumArrays(data.datasets.usageFromCommunity, data.datasets.usageFromSupplier),
      ...(inverterIndex > 0 ? data.datasets.usageInverterProduction : []),
      ...data.datasets.forecastBalance
    )

    const minValue = Math.min(
      ...sumArrays(data.datasets.usageToCommunity, data.datasets.usageToSupplier),
      ...data.datasets.forecastBalance
    )

    for (let i = 0; i < chartSettings.options.scales.yAxes.length; i++) {
      chartSettings.options.scales.yAxes[i].ticks.suggestedMax = maxValue
      chartSettings.options.scales.yAxes[i].ticks.suggestedMin = minValue
    }

    if (!chartJs.value) {
      chartJs.value = new Chart(chartEl.value, chartSettings) as ChartJS
    } else {
      chartJs.value.data = chartSettings.data
      chartJs.value.options = chartSettings.options
      chartJs.value.update()
    }

    console.debug('Month chart values', chartSettings)
  }

  function validateData(data: HistoryDataState) {
    if (!data.datasets) {
      return false
    }

    const now = moment()
    const isDataInFuture = moment(data.labels[0]) > now

    const values = [
      ...(data.datasets.fromCommunity ?? []),
      ...(data.datasets.fromSupplier ?? []),
      ...(data.datasets.toCommunity ?? []),
      ...(data.datasets.toSupplier ?? []),
      ...(data.datasets.communityConsumable ?? [])
    ]

    const anyNonZero = values.filter((val) => val != undefined && val != 0).length > 0

    return anyNonZero || isDataInFuture
  }

  function updateDayChart(_data, config): void {
    const data = JSON.parse(JSON.stringify(_data))

    // Dont show/update the chart without any data.
    // if (!validateData(data)) {
    //   return false
    // }

    if ((data.labels?.length ?? 0) < 1) {
      return
    }

    const chartSettings: ChartConfiguration = {
      ...config
    }

    chartSettings.plugins = [
      ...(chartSettings.plugins ?? []),
      {
        afterUpdate(chart: any) {
          const y = chart.scales['yAbove']
          const yPercent = chart.scales['yPercent']

          if (y && yPercent) {
            const min = y.min
            const max = y.max

            const oldMin = yPercent.options.ticks.min
            const oldMax = yPercent.options.ticks.max
            const newMin = (100 * min) / max
            const newMax = 100

            if (oldMin != newMin && oldMax != newMax) {
              yPercent.options.ticks.min = newMin
              yPercent.options.ticks.max = newMax

              // not ideal because it triggers a re-render, but better than nothing
              chart.update()
            }
          }
        }
      }
    ]

    chartSettings.data.labels = data.labels
    chartSettings.data.datasets[0].data = data.datasets.fromCommunity
    chartSettings.data.datasets[1].data = data.datasets.fromSupplier
    chartSettings.data.datasets[2].data = data.datasets.toCommunity
    chartSettings.data.datasets[3].data = data.datasets.toSupplier
    chartSettings.data.datasets[4].data = data.datasets.communityConsumable

    const batteryIndex = chartSettings.data.datasets.findIndex((dataset) => {
      return dataset.label?.toLowerCase().includes('batterie')
    })
    const inverterIndex = chartSettings.data.datasets.findIndex((dataset) => {
      return dataset.label?.toLowerCase().includes('inverter') || dataset.label?.toLowerCase().includes('wechselrichter')
    })

    if (inverterIndex > 0 && chartSettings.data.datasets[inverterIndex]) {
      chartSettings.data.datasets[inverterIndex].data = data.datasets.inverterProduction
    }

    if (batteryIndex > 0 && chartSettings.data.datasets[batteryIndex]) {
      chartSettings.data.datasets[batteryIndex].data = data.datasets.batterySOC
    }

    const maxValue = Math.max(
      ...sumArrays(data.datasets.fromCommunity, data.datasets.fromSupplier),
      ...(data.datasets.inverterProduction ?? [])
    )

    const minValue = Math.min(
      ...sumArrays(data.datasets.toCommunity, data.datasets.toSupplier),
      ...data.datasets.communityConsumable
    )

    for (let i = 0; i < chartSettings.options.scales.yAxes.length; i++) {
      const yAxis = chartSettings.options.scales.yAxes[i]

      if (yAxis.id != 'yPercent') {
        yAxis.ticks.suggestedMax = maxValue
        yAxis.ticks.suggestedMin = minValue
      } else if (yAxis.id == 'yPercent') {
        // const maxRounded = Math.ceil(maxValue / 1000) * 1000
        // const minRounded = Math.floor(minValue / 1000) * 1000
        // yAxis.ticks.suggestedMax = 100
        // yAxis.ticks.suggestedMin = (100 * minRounded) / maxRounded
      }
    }

    if (!chartJs.value) {
      chartJs.value = new Chart(chartEl.value, chartSettings) as ChartJS
      setupEventHandlers(chartJs.value, chartEl.value)
    } else {
      chartJs.value.data = chartSettings.data
      chartJs.value.options = chartSettings.options
    }

    chartJs.value.update()

    console.debug('Day chart values', data)
  }

  const onResize = () => {
    if (chartJs.value?.options?.legend?.display) {
      chartJs.value.options.legend.display = window.innerHeight >= 500
    }
  }

  onMounted(() => {
    window.addEventListener('resize', onResize)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', onResize)
  })

  return {
    updateMonthChart,
    updateDayChart,
    chartJs,
    setupEventHandlers
  }
}
